import { IPreviewTags, IPreviewTemplate } from '@/components/TemplatePreview';
import { ArabicTimezones } from '@/utils/enum';

const normalizeTemplateText = (value: string | null | undefined): string => {
  if (value == null) return '';

  const trimmed = value.trim();
  if (!trimmed) return '';

  const lowered = trimmed.toLowerCase();
  if (lowered === 'undefined' || lowered === 'null') return '';

  return value;
};

const injectAgentData = (template: string, agent: IPreviewTags) => {
  return template.replace(/{{(.*?)}}/g, (_, key) => {
    const value = agent[key.trim()];

    if (key.trim() === 'location_logo' && value) {
      return `<img src="${value}" style="width:auto; height: ${agent.logo_height ? `${agent.logo_height}vh` : '7vh'};" alt="Location Logo" />`;
    }

    if (
      typeof value === 'string' &&
      ['undefined', 'null'].includes(value.trim().toLowerCase())
    ) {
      return '';
    }

    return value != null ? value : `{{${key}}}`;
  });
};

const injectStyles = (html: string): string => {
  const tagStyles: Record<string, string> = {
    h1: 'font-size:2em;line-height:1.2;margin:0.67em 0;display:block;',
    h2: 'font-size:1.5em;line-height:1.2;margin:0.75em 0;display:block;',
    h3: 'font-size:1.17em;line-height:1.2;margin:0.83em 0;display:block;',
    h4: 'font-size:1em;line-height:1.2;margin:1.12em 0;display:block;',
    h5: 'font-size:0.83em;line-height:1.2;margin:1.5em 0;display:block;',
    h6: 'font-size:0.75em;line-height:1.2;margin:1.67em 0;display:block;',
    ul: 'list-style-type:disc;margin:0.5em 0;padding-left:2em;display:block;',
    ol: 'list-style-type:decimal;margin:0.5em 0;padding-left:2em;display:block;',
    li: 'display:list-item;margin:0.25em 0;',
  };

  let result = html;

  for (const [tag, styles] of Object.entries(tagStyles)) {
    result = result.replace(
      new RegExp(`<${tag}(\\s[^>]*)?>`, 'gi'),
      (match, attrs = '') => {
        if (/style\s*=/i.test(attrs)) {
          return match.replace(
            /style\s*=\s*["']([^"']*)["']/i,
            (_, existing) => `style="${styles}${existing}"`
          );
        }
        return `<${tag}${attrs} style="${styles}">`;
      }
    );
  }

  return result;
};

const createHeader = (header: string | null, agent: IPreviewTags) => {
  const normalizedHeader = normalizeTemplateText(header);
  if (!normalizedHeader) return '';

  const injectedHeader = injectAgentData(normalizedHeader, agent);
  const styledHeader = injectStyles(injectedHeader);
  const containsArabic = /[\u0600-\u06FF]/.test(styledHeader);
  const alignment = containsArabic ? 'text-align: right; direction: rtl;' : '';

  return `<div style="${alignment}">${styledHeader.replace(/\n/g, '<br>')}</div>`;
};

const createArabicContent = (
  arabicContent: string | null,
  agent: IPreviewTags
) => {
  const normalizedArabicContent = normalizeTemplateText(arabicContent);
  if (!normalizedArabicContent) return '';

  const injectedContent = injectAgentData(normalizedArabicContent, agent);
  const styledContent = injectStyles(injectedContent);
  const containsArabic = /[\u0600-\u06FF]/.test(styledContent);
  const alignment = containsArabic ? 'text-align: right; direction: rtl;' : '';

  return `<div style="${alignment}">${styledContent.replace(/\n/g, '<br>')}</div>`;
};

const createFooter = (footer: string | null, agent: IPreviewTags) => {
  const normalizedFooter = normalizeTemplateText(footer);
  if (!normalizedFooter) return '';

  const injectedFooter = injectAgentData(normalizedFooter, agent);
  const styledFooter = injectStyles(injectedFooter);
  const containsArabic = /[\u0600-\u06FF]/.test(styledFooter);
  const alignment = containsArabic ? 'text-align: right; direction: rtl;' : '';

  return `<div style="font-size:10px; ${alignment}">${styledFooter.replace(/\n/g, '<br>')}</div>`;
};

function previewTemplate(template: IPreviewTemplate, agent: IPreviewTags) {
  let html_content = `
   <!DOCTYPE HTML
    PUBLIC "-//W3C//DTD XHTML 1.0 Transitional //EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">
  <html xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml"
    xmlns:o="urn:schemas-microsoft-com:office:office">
  
  <head> <!--[if gte mso 9]>
    <xml>
      <o:OfficeDocumentSettings>
        <o:AllowPNG/>
        <o:PixelsPerInch>96</o:PixelsPerInch>
      </o:OfficeDocumentSettings>
    </xml>
    <![endif]-->
    <meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
    <meta name="viewport" content="width=device-width,initial-scale=1">
    <meta name="x-apple-disable-message-reformatting"> <!--[if !mso]><!-->
    <meta http-equiv="X-UA-Compatible" content="IE=edge"><!--<![endif]-->
    <title></title>
    <style type="text/css">
        @media only screen and (min-width:620px) {
            .u-row {
                width: 600px !important
            }
  
            .u-row .u-col {
                vertical-align: top
            }
  
            .u-row .u-col-100 {
                width: 600px !important
            }
        }
  
        @media only screen and (max-width:620px) {
            .u-row-container {
                max-width: 100% !important;
                padding-left: 0 !important;
                padding-right: 0 !important
            }
  
            .u-row {
                width: 100% !important
            }
  
            .u-row .u-col {
                display: block !important;
                width: 100% !important;
                min-width: 320px !important;
                max-width: 100% !important
            }
  
            .u-row .u-col>div {
                margin: 0 auto
            }
  
            .u-row .u-col img {
                max-width: 100% !important
            }
        }
  
        body {
            margin: 0;
            padding: 0
        }
  
        table,
        td,
        tr {
            border-collapse: collapse;
            vertical-align: top
        }
  
        p {
            margin: 0
        }
  
        .ie-container table,
        .mso-container table {
            table-layout: fixed
        }
  
        * {
            line-height: inherit
        }
  
        a[x-apple-data-detectors=true] {
            color: inherit !important;
            text-decoration: none !important
        }
  
        table,
        td {
            color: #000
        }
  
        #u_body a {
            color: #3598db;
            text-decoration: underline
        }
    </style> <!--[if !mso]><!-->
    <link href="https://fonts.googleapis.com/css2?family=Noto+Sans:wght@400;700" rel="stylesheet" type="text/css">
    <!--<![endif]-->
  </head>
  
  <body class="clean-body u_body"
    style="margin:0;padding:0;-webkit-text-size-adjust:100%;background-color:#fff;color:#000">
    <!--[if IE]><div class="ie-container"><![endif]--> <!--[if mso]><div class="mso-container"><![endif]-->
    <table id="u_body"
        style="border-collapse:collapse;table-layout:fixed;border-spacing:0;mso-table-lspace:0;mso-table-rspace:0;vertical-align:top;min-width:320px;Margin:0 auto;background-color:#fff;width:100%"
        cellpadding="0" cellspacing="0">
        <tbody>
            <tr style="vertical-align:top">
                <td style="word-break:break-word;border-collapse:collapse!important;vertical-align:top">
                    <!--[if (mso)|(IE)]><table width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td align="center" style="background-color: #ffffff;"><![endif]-->
                    <div class="u-row-container" style="padding:0;background-color:transparent">
                        <div class="u-row"
                            style="margin:0 auto;min-width:320px;max-width:600px;overflow-wrap:break-word;word-wrap:break-word;word-break:break-word;background-color:transparent">
                            <div
                                style="border-collapse:collapse;display:table;width:100%;height:100%;background-color:transparent">
                                <!--[if (mso)|(IE)]><table width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td style="padding: 0px;background-color: transparent;" align="center"><table cellpadding="0" cellspacing="0" border="0" style="width:600px;"><tr style="background-color: transparent;"><![endif]-->
                                <!--[if (mso)|(IE)]><td align="center" width="600" style="width: 600px;padding: 0px;border-top: 0px solid transparent;border-left: 0px solid transparent;border-right: 0px solid transparent;border-bottom: 0px solid transparent;" valign="top"><![endif]-->
                                <div class="u-col u-col-100"
                                    style="max-width:320px;min-width:600px;display:table-cell;vertical-align:top">
                                    <div style="height:100%;width:100%!important"> <!--[if (!mso)&(!IE)]><!-->
                                        <div
                                            style="box-sizing:border-box;height:100%;padding:0;border-top:0 solid transparent;border-left:0 solid transparent;border-right:0 solid transparent;border-bottom:0 solid transparent">
                                            <!--<![endif]-->
                                            <table style="font-family:'Noto Sans',sans-serif" role="presentation"
                                                cellpadding="0" cellspacing="0" width="100%" border="0">
                                                <tbody>
                                                    <tr>
                                                        <td style="overflow-wrap:break-word;word-break:break-word;padding:10px;font-family:'Noto Sans',sans-serif"
                                                            align="left">
                                                            <div
                                                                style="font-size:14px;line-height:140%;text-align:left;word-wrap:break-word">
                                                                <p style="font-size:14px;line-height:140%">&nbsp;</p 
                                                                ${
                                                                  template.header
                                                                    ? `<p style="font-size:14px;line-height:140%">${createHeader(template.header, agent)}</p>
                                                                <p style="font-size:14px;line-height:140%">&nbsp;</p>`
                                                                    : ''
                                                                }
                                                                ${template.timezone && template.arabicContent && ArabicTimezones.includes(template.timezone as string) ? `<p style="font-size:14px;line-height:140%">${createArabicContent(template.arabicContent, agent)}</p>` : ''}

                                                                ${
                                                                  template?.footer &&
                                                                  typeof template.footer ===
                                                                    'string'
                                                                    ? `<p style="font-size:10px;line-height:140%">${createFooter(template.footer, agent)}</p>`
                                                                    : ''
                                                                }
                                                            </div>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div><!--<![endif]-->
                                    </div>
                                </div> <!--[if (mso)|(IE)]></td><![endif]-->
                                <!--[if (mso)|(IE)]></tr></table></td></tr></table><![endif]-->
                            </div>
                        </div>
                    </div> <!--[if (mso)|(IE)]></td></tr></table><![endif]-->
                </td>
            </tr>
        </tbody>
    </table> <!--[if mso]></div><![endif]--> <!--[if IE]></div><![endif]-->
  </body>
  
  </html>
  `;

  return html_content;
}

export { previewTemplate };
